import { z } from 'zod';
import { isoDateSchema } from './common.js';

/** Token prefixes tell the two kinds apart (and make leaked keys easy to grep for). */
export const API_KEY_PREFIX = { client: 'hcl_', application: 'hap_' } as const;

/** What an application key may touch. Each resource is `none`, `read` or `write` (write includes read). */
export const APPLICATION_RESOURCES = [
  'users',
  'nodes',
  'locations',
  'nests',
  'game-profiles',
  'servers',
  'database-hosts',
  'settings',
  'activity',
  'ai',
  'customisation',
  'tenants',
] as const;
export type ApplicationResource = (typeof APPLICATION_RESOURCES)[number];

export const apiKeyAccessSchema = z.enum(['none', 'read', 'write']);
export type ApiKeyAccess = z.infer<typeof apiKeyAccessSchema>;

export const apiKeyScopesSchema = z.partialRecord(
  z.enum(APPLICATION_RESOURCES),
  apiKeyAccessSchema,
);
export type ApiKeyScopes = z.infer<typeof apiKeyScopesSchema>;

/** Plain IPs or CIDR blocks, IPv4 or IPv6. An empty list allows any address. */
export const allowedIpsSchema = z.array(z.string().trim().min(1).max(64)).max(32).default([]);

export const apiKeySchema = z.object({
  id: z.number().int(),
  kind: z.enum(['client', 'application']),
  /** The public, non-secret part of the token. */
  identifier: z.string(),
  memo: z.string(),
  allowedIps: z.array(z.string()),
  scopes: apiKeyScopesSchema,
  lastUsedAt: isoDateSchema.nullable(),
  lastUsedIp: z.string().nullable(),
  expiresAt: isoDateSchema.nullable(),
  createdAt: isoDateSchema,
});
export type ApiKey = z.infer<typeof apiKeySchema>;

/** Returned once, when the key is created. */
export const createdApiKeySchema = apiKeySchema.extend({ token: z.string() });
export type CreatedApiKey = z.infer<typeof createdApiKeySchema>;

const baseCreate = {
  memo: z.string().trim().min(1, 'Describe what this key is for.').max(191),
  allowedIps: allowedIpsSchema,
  /** Days until the key stops working; omit for no expiry. */
  expiresInDays: z.number().int().min(1).max(3650).optional(),
};

export const createClientApiKeySchema = z.object(baseCreate);
export const createApplicationApiKeySchema = z.object({
  ...baseCreate,
  scopes: apiKeyScopesSchema,
});
